import { FC, useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import useSkippedOrders from "../hooks/useSkippedOrders";
import SkippedOrdersModal from "./SkippedOrdersModal";
import { SkippedOrdersIcon } from "./icons";

export interface SkippedOrdersButtonProps {
  date: string;
}

const SkippedOrdersButton: FC<SkippedOrdersButtonProps> = ({ date }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Frozen at modal-open time, so the modal (and any temp route created
  // from it) doesn't silently follow a live date change (e.g. End Shift).
  const [modalDate, setModalDate] = useState(date);

  // Badge always reflects the live date; modal content stays pinned.
  const { totalCount, isLoading } = useSkippedOrders(date);
  const { regionGroups: modalRegionGroups, refetch } =
    useSkippedOrders(modalDate);

  const openModal = () => {
    setModalDate(date);
    setIsModalOpen(true);
  };

  const hasNothingToShow = !isLoading && totalCount === 0;

  if (!isModalOpen && hasNothingToShow) {
    return null;
  }

  return (
    <>
      <Tooltip title="Skipped Orders">
        {/* Span wrapper: MUI Tooltip can't attach hover listeners directly to
            a disabled button (disabled elements fire no pointer events). */}
        <Box component="span">
          <Button
            variant="outlined"
            startIcon={<SkippedOrdersIcon />}
            onClick={openModal}
            disabled={isLoading}
            data-testid="skipped-orders-button"
            sx={{ color: "#FFE2AB" }}
          >
            {totalCount}
          </Button>
        </Box>
      </Tooltip>
      {isModalOpen && (
        <SkippedOrdersModal
          date={modalDate}
          regionGroups={modalRegionGroups}
          onClose={() => setIsModalOpen(false)}
          onRestored={refetch}
        />
      )}
    </>
  );
};

export default SkippedOrdersButton;
